import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Slide,
  TextField,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { UPDATE_CONTRACT_PAYDAY } from 'src/store/actions/contractActions';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from './styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PayDayDialog = ({ open, handleClose, submit, payDayData }) => {
  const { classes } = useStyles();
  const initialState = makeInitialState({ payday: payDayData });
  const resetRef = useRef();
  const { t } = useTranslation();
  const loading = useSelector(
    isLoadingSelector([UPDATE_CONTRACT_PAYDAY.action])
  );

  // Neither resets nor closes here: the form has to stay on screen with its
  // values while the request is in flight, because that is what the save
  // button's loading state is reporting on. Closing is the action's job (via
  // the close callback), and the effect below resets once it actually closes.
  const handleSubmitPayDay = useCallback(
    values => {
      submit(values);
    },
    [submit]
  );

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.paper }}
      fullWidth
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <Formik
        enableReinitialize
        initialValues={initialState}
        validationSchema={validationSchema}
        onSubmit={handleSubmitPayDay}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          resetForm
        }) => {
          resetRef.current = () => resetForm();
          const paydayError = Boolean(touched.payday && errors.payday);
          const descriptionError = Boolean(
            touched.description && errors.description
          );

          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle
                className={classes.header}
                id="pay-day-dialog-title"
                component="div"
              >
                <Box>
                  <Typography variant="overline" className={classes.eyebrow}>
                    {t('CONTRACT_VIEW.PAY_DAY_DIALOG_EYEBROW')}
                  </Typography>
                  <Typography variant="h3" className={classes.title}>
                    {t('CONTRACT_VIEW.EDIT_PAY_DAY')}
                  </Typography>
                  <Typography variant="body2" className={classes.subtitle}>
                    {t('CONTRACT_VIEW.PAY_DAY_DIALOG_SUBTITLE')}
                  </Typography>
                </Box>
                <IconButton
                  key="close"
                  aria-label="Close"
                  color="inherit"
                  onClick={handleClose}
                  className={classes.closeIcon}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent className={classes.content}>
                <Box className={classes.field}>
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="payday"
                    className={clsx(
                      classes.fieldLabel,
                      paydayError && classes.fieldLabelError
                    )}
                  >
                    {t('COMMON.PAY_DAY')}
                  </Typography>
                  <TextField
                    id="payday"
                    inputProps={{
                      min: 1,
                      max: 31,
                      autoComplete: 'off'
                    }}
                    error={paydayError}
                    fullWidth
                    name="payday"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="number"
                    value={values.payday}
                    variant="outlined"
                  />
                  {paydayError ? (
                    <Typography variant="caption" className={classes.errorText}>
                      <span className={classes.errorDot} />
                      {t(errors.payday)}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      className={classes.helperText}
                    >
                      {t('CONTRACT_VIEW.PAY_DAY_HELPER')}
                    </Typography>
                  )}
                </Box>

                <Box className={classes.field}>
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="description"
                    className={clsx(
                      classes.fieldLabel,
                      descriptionError && classes.fieldLabelError
                    )}
                  >
                    {t('CONTRACT_VIEW.PAY_DAY_CHANGE_REASON')}
                  </Typography>
                  <TextField
                    id="description"
                    error={descriptionError}
                    fullWidth
                    multiline
                    minRows={4}
                    name="description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    variant="outlined"
                  />
                  {descriptionError && (
                    <Typography variant="caption" className={classes.errorText}>
                      <span className={classes.errorDot} />
                      {t(errors.description)}
                    </Typography>
                  )}
                </Box>

                <Box className={classes.impact}>
                  <InfoOutlinedIcon
                    fontSize="small"
                    className={classes.impactIcon}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      className={classes.impactTitle}
                    >
                      {t('CONTRACT_VIEW.PAY_DAY_CHANGE_IMPACT_TITLE')}
                    </Typography>
                    <Typography variant="body2">
                      {t('CONTRACT_VIEW.PAY_DAY_CHANGE_NOTICE')}
                    </Typography>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions className={classes.actions}>
                <Button
                  className={clsx(classes.button, classes.secondaryButton)}
                  disabled={loading}
                  onClick={handleClose}
                  type="button"
                >
                  {t('COMMON.CANCEL')}
                </Button>
                <Button
                  className={clsx(classes.button, classes.primaryButton)}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                  type="submit"
                >
                  {loading ? t('COMMON.SAVING') : t('COMMON.SAVE')}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

PayDayDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  payDayData: PropTypes.number
};

export default PayDayDialog;
